<template>
  <div
    class="sf-bg-white sf-full-height tw-overflow-auto"
    data-testid="sf-insights-left-panel-lite"
  >
    <div class="sf-pad-v-2 sf-layer-border-bottom">
      <div class="sf-pad-h-3 tw-flex tw-gap-4 tw-w-full tw-mb-4">
        <sf-button-icon icon="arrow-left" @click="closeDrawer" />
      </div>
      <div class="sf-pad-h-3">
        <segment-selector />
        <div class="sf-bg-snow sf-layer-rounded sf-pad-2">
          <div class="sf-uppercase sf-title sf-font-xs sf-below-1">
            {{ filtersAppliedLabel }}
          </div>
          <div v-show="activeFilters.length">
            <active-filters-list
              :edit-filter="togglePremiumModal"
              :immutable="true"
            ></active-filters-list>
          </div>
          <div
            v-show="activeFilters.length == 0"
            class="sf-tab-item sf-c-text-darker sf-pad-0 sf-below-1 sf-hover"
            style="background-color: #f1f3f5"
          >
            <div class="sf-level sf-level-between sf-pad-1">
              <div class="sf-level-item">
                <div>{{ clientsLabel }}</div>
                <div class="sf-font-light">{{ showAllLabel }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="sf-pad-v-3 sf-pad-h-3 sf-layer-border-bottom sf-below-2">
        <div class="sf-title sf-font-l sf-below-1">
          {{ periodSelectionLabel }}
          <div class="sf-label sf-label-primary">
            {{ premiumLabel }}
          </div>
        </div>
        <template v-if="periodSelection">
          <div
            v-dompurify-html="
              I18n.t(
                'companies_overview.period_selection.short.' + periodSelection,
                { date: ledgerSelectDate || today }
              )
            "
            class="sf-pad-b-2"
          />
        </template>
        <div
          class="
            sf-btn sf-btn-primary sf-btn-inverted sf-alignc sf-full-width
            rs-add-trigger
            sf-level sf-level-around sf-c-primary
          "
          data-heap="premium-insight-period"
          @click="togglePremiumModal"
        >
          <div>{{ periodSelectionButton }}</div>
        </div>
      </div>
      <div class="sf-pad-v-3 sf-pad-h-3 sf-layer-border-bottom">
        <div class="sf-title sf-font-l sf-below-1">
          {{ communicationTriggersTitle }}
          <div class="sf-label sf-label-primary">
            {{ premiumLabel }}
          </div>
        </div>
        <div class="sf-font-1 sf-below-3 sf-c-text-darker">
          {{ communicationTriggersDesc }}
        </div>
        <a
          data-heap="premium-insight-trigger"
          class="
            sf-btn
            sf-btn-primary
            sf-btn-inverted
            sf-alignc
            sf-full-width
            sf-level
            sf-level-center
          "
          @click="togglePremiumModal"
          >{{ addCommunicationTriggerButton }}</a
        >
      </div>
      <div class="sf-pad-h-3 sf-pad-v-2">
        <div class="sf-title sf-font-l sf-below-1">
          {{ allFiltersTitle }}
          <div class="sf-label sf-label-primary">
            {{ premiumLabel }}
          </div>
        </div>
        <div class="sf-font-1 sf-below-2 sf-c-text-darker">
          {{ allFiltersDesc }}
        </div>
        <filters-list
          :edit-filter="togglePremiumModal"
          :filters="allFilters"
        ></filters-list>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import { useDrawer } from '@app/shared/pinia/drawer'

import FiltersList from './filters/filters-list'
import ActiveFiltersList from './filters/active-filters-list'
import SegmentSelector from './segments/segment-selector-lite'
import { SfTime } from '@app/adapters/time'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button/index'

export default {
  components: {
    'filters-list': FiltersList,
    'active-filters-list': ActiveFiltersList,
    'segment-selector': SegmentSelector,
    'sf-button-icon': SfButtonIcon,
  },

  computed: {
    ...mapGetters([
      'manageTrigger',
      'getFilterByType',
      'isSegmentModified',
      'getError',
      'allCompaniesProcessed',
    ]),

    ...mapState({
      allFilters: (state) => state.filters.allFilters,
      activeFilters: (state) => state.filters.activeFilters,
      periodSelection: (state) => state.periodSelection,
      ledgerSelectDate: (state) => state.ledgerSelectDate,
    }),

    ...mapPiniaState(useDrawer, ['isLeftDrawerOpen']),

    showEmptyCompanies() {
      return this.allCompaniesProcessed && !Object.keys(this.companies).length
    },

    manageTrigger() {
      return Silverfin.isCurrentUserAdmin()
    },

    filtersAppliedLabel() {
      return I18n.t('companies_overview_lite.filters_applied')
    },

    clientsLabel() {
      return I18n.t('companies_overview_lite.clients')
    },

    showAllLabel() {
      return I18n.t('companies_overview_lite.show_all')
    },

    communicationTriggersTitle() {
      return I18n.t('companies_overview_lite.communication_triggers')
    },

    premiumLabel() {
      return I18n.t('companies_overview_lite.premium')
    },

    communicationTriggersDesc() {
      return I18n.t('companies_overview_lite.communication_triggers_desc')
    },

    addCommunicationTriggerButton() {
      return I18n.t('companies_overview_lite.add_communication_trigger')
    },

    allFiltersTitle() {
      return I18n.t('companies_overview_lite.all_filters')
    },

    allFiltersDesc() {
      return I18n.t('companies_overview_lite.all_filters_desc')
    },

    periodSelectionLabel() {
      return I18n.t('companies_overview.label_period_selection')
    },

    periodSelectionButton() {
      return I18n.t('companies_overview_lite.period_selection_button')
    },

    today: () =>
      I18n.t(
        'companies_overview.period_selection_new.date_selection_placeholder_with_date',
        { date: SfTime(new Date()).format('DD/MM/YYYY') }
      ),
  },

  methods: {
    ...mapActions(['togglePremiumModal']),
    ...mapPiniaActions(useDrawer, ['closeDrawer']),
  },
}
</script>
